import { useState, useCallback, useRef } from "react";
import { useUser } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { api, buildQueryString } from "@/lib/api";
import { X, ImagePlus, Loader2, Images } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Video } from "@/types";
import { isExpired } from "@/config/retention";
import { checkImageFile, UnsupportedFileModal, type FileProblem } from "@/components/ui/unsupported-file-modal";

interface UploadZoneProps {
  imageUrl: string | null;
  onImageChange: (url: string | null, meta?: { width: number; height: number }) => void;
}

export function UploadZone({ imageUrl, onImageChange }: UploadZoneProps) {
  const { user } = useUser();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showAssets, setShowAssets] = useState(false);
  const [fileProblem, setFileProblem] = useState<FileProblem | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);


  const uploadFile = useCallback(
    async (file: File) => {
      setIsUploading(true);
      try {
        const dims = await new Promise<{ width: number; height: number }>((resolve, reject) => {
          const img = new Image();
          const objUrl = URL.createObjectURL(file);
          img.onload = () => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
            URL.revokeObjectURL(objUrl);
          };
          img.onerror = () => {
            URL.revokeObjectURL(objUrl);
            reject(new Error("Failed to read image"));
          };
          img.src = objUrl;
        }).catch(() => null);

        const formData = new FormData();
        formData.append("file", file);
        if (user?.id) formData.append("user_id", user.id);
        const result = await api.upload<{ success: boolean; url: string }>("/upload", formData);
        onImageChange(result.url, dims ?? undefined);
      } catch (err) {
        console.error("Upload failed:", err);
      } finally {
        setIsUploading(false);
      }
    },
    [user?.id, onImageChange],
  );

  // Checked before uploading: the gateway only takes JPEG, PNG, WebP or HEIC up to 50 MB.
  const pickFile = useCallback((file: File) => {
    const problem = checkImageFile(file);
    if (problem) setFileProblem(problem);
    else void uploadFile(file);
  }, [uploadFile]);
  const chooseAnother = useCallback(() => {
    setFileProblem(null);
    fileInputRef.current?.click();
  }, []);
  const fileModal = <UnsupportedFileModal problem={fileProblem} onClose={() => setFileProblem(null)} onChooseAnother={chooseAnother} />;

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) pickFile(file);
    },
    [pickFile],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = ""; // so choosing the same file again still fires
      if (file) pickFile(file);
    },
    [pickFile],
  );

  if (imageUrl) {
    return (
      <div className="relative rounded-2xl overflow-hidden border border-white/10 group">
        <img src={imageUrl} alt="Character" className="w-full max-h-72 object-contain bg-black/20" onError={() => onImageChange(null)} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <button
          onClick={() => onImageChange(null)}
          className="absolute top-3 right-3 p-1.5 rounded-xl bg-black/50 backdrop-blur-sm text-white/70 hover:text-white hover:bg-black/60 transition-all border border-white/10"
        >
          <X className="size-4" />
        </button>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="absolute bottom-3 right-3 px-3 py-1.5 rounded-xl bg-black/50 backdrop-blur-sm text-white/80 hover:bg-black/60 transition-all border border-white/10 text-xs font-semibold md:opacity-0 md:group-hover:opacity-100"
        >
          Replace
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
        {fileModal}
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "relative flex flex-col items-center justify-center py-8 px-4 rounded-2xl border-2 border-dashed cursor-pointer transition-all duration-300",
            isDragging
              ? "border-[#d4a574]/40 card-glass"
              : "border-white/10 card-glass hover:border-white/18",
          )}
        >
          {isUploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="size-5 animate-spin text-[#d4a574] mb-2" />
              <p className="text-[13px] text-muted-foreground font-semibold">Uploading...</p>
            </div>
          ) : (
            <>
              <ImagePlus className="size-7 text-[#d4a574]/50 mb-2.5" strokeWidth={1.5} />
              <p className="text-[13px] text-foreground font-semibold text-center">Upload character image <span className="text-muted-foreground font-normal">(optional)</span></p>
              <p className="text-[11px] text-muted-foreground mt-1 text-center">Drop or click to browse</p>
            </>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
        </div>

        <button
          onClick={() => setShowAssets(true)}
          className="flex flex-col items-center justify-center py-8 px-4 rounded-2xl border border-white/8 card-glass hover:border-white/12 transition-all cursor-pointer"
        >
          <Images className="size-7 text-[#d4a574]/50 mb-2.5" />
          <p className="text-[13px] text-foreground font-semibold text-center">Select from assets <span className="text-muted-foreground font-normal">(optional)</span></p>
          <p className="text-[11px] text-muted-foreground mt-1 text-center">Choose an existing character</p>
        </button>
      </div>

      {fileModal}
      {showAssets && (
        <AssetModal
          onSelect={(url) => { onImageChange(url); setShowAssets(false); }}
          onClose={() => setShowAssets(false)}
        />
      )}
    </div>
  );
}

function AssetModal({ onSelect, onClose }: { onSelect: (url: string) => void; onClose: () => void }) {
  const { user } = useUser();
  const [brokenUrls, setBrokenUrls] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ["videos", user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error("No user");
      return api.get<{ success: boolean; videos: Video[] }>(
        `/videos${buildQueryString({ user_id: user.id })}`,
      );
    },
    enabled: !!user?.id,
  });

  const completed = (data?.videos ?? []).filter(
    (v) => v.status === "completed" && v.url && v.media_type === "IMAGE" && !isExpired(v.created_at) && !brokenUrls.has(v.url!),
  );

  return (
    <div className="mt-3 rounded-2xl border border-white/8 card-glass overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/6">
        <p className="text-[12px] font-semibold text-foreground">Select a character</p>
        <button onClick={onClose} className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">
          <X className="size-3.5" />
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="size-5 animate-spin text-[#d4a574]" />
        </div>
      ) : completed.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center px-4">
          <Images className="size-6 text-[#d4a574]/30 mb-2" />
          <p className="text-[12px] font-semibold text-foreground mb-0.5">No characters yet</p>
          <p className="text-[11px] text-muted-foreground">Create a character first, then select it here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 p-2 max-h-[240px] overflow-y-auto">
          {completed.map((v) => (
            <button
              key={v.id}
              onClick={() => onSelect(v.url!)}
              className="relative aspect-square rounded-xl overflow-hidden border border-white/6 hover:border-[#d4a574]/30 hover:ring-2 hover:ring-[#d4a574]/20 transition-all group"
            >
              {v.media_type === "IMAGE" ? (
                <img src={v.url!} alt={v.prompt} className="w-full h-full object-cover" onError={() => setBrokenUrls((prev) => new Set(prev).add(v.url!))} />
              ) : (
                <video src={`${v.url!}#t=0.1`} className="w-full h-full object-cover" muted playsInline preload="metadata" />
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
